/*
 *******************************************************************************
 *
 *  Filename:   @/contexts/JWTContext.js
 *
 *  Syntax:     NA
 *
 *  Synopsis:   JWT Context for authentication.
 *
 *  Author:     Norman J. Nolasco [ Archetact Software ]
 *
 *  Created:    Saturday, August 19, 2023 - 10:00 PM (CST)
 *
 *  Notes:
 *
 *  Revisions:
 *              2023/08/19  NJN Initial Build
 *
 *  Copyright (c) 2023 - Archetact Software - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */

import { createContext, useEffect, useReducer } from "react";
import { initializer } from "./shared/initializer";
import reducer from "./shared/reducer";
import {
  signIn,
  signOut,
  signUp,
  resetPassword,
  completeResetPassword,
  completeNewPasswordChallenge,
  confirmSignUp,
  resendConfirmationCode,
} from "./shared/actions";

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  authToken: null,
  user: null,
  newPasswordRequired: false,
  resetPasswordRequired: false,
};

const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    initializer(dispatch);
  }, []);

  /*
  useEffect(() => {
    console.log("AuthContext state", state);
  }, [state]);
  */

  const signInWrapper = async (email, password) => {
    const response = await signIn(email, password, dispatch);
    return response;
  };

  const signOutWrapper = async () => {
    const response = await signOut(dispatch);
    return response;
  };

  const signUpWrapper = async (firstname, lastname, company, email, phone, password) => {
    const response = await signUp(firstname, lastname, company, email, phone, password, dispatch);
    return response;
  };

  const resetPasswordWrapper = async (email) => {
    const response = await resetPassword(email, dispatch);
    return response;
  };

  const completeResetPasswordWrapper = async (email, newPassword, confirmationCode) => {
    const response = await completeResetPassword(email, newPassword, confirmationCode, dispatch);
    return response;
  };

  const completeNewPasswordChallengeWrapper = async (email, password, session) => {
    const response = await completeNewPasswordChallenge(email, password, session, dispatch);
    return response;
  };

  const confirmSignUpWrapper = async (email, confirmationCode) => {
    const response = await confirmSignUp(email, confirmationCode, dispatch);
    return response;
  };

  const resendConfirmationCodeWrapper = async (email) => {
    const response = await resendConfirmationCode(email, dispatch);
    return response;
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        signIn: signInWrapper,
        signOut: signOutWrapper,
        signUp: signUpWrapper,
        resetPassword: resetPasswordWrapper,
        completeResetPassword: completeResetPasswordWrapper,
        completeNewPasswordChallenge: completeNewPasswordChallengeWrapper,
        confirmSignUp: confirmSignUpWrapper,
        resendConfirmationCode: resendConfirmationCodeWrapper,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
